import { useEffect, useState, useRef } from "react";
import { Bell, BellOff, MessageSquare, X, CheckCheck, ArrowRight, Settings2, Volume2, VolumeX } from "lucide-react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";

// Web-Audio chime — three variants, no assets needed
const CHIME_PRESETS = {
  soft:        { type: "sine",     notes: [{ f: 784,    t: 0 }, { f: 1046.5, t: 0.14 }], peak: 0.22, tail: 0.35 },
  sharp:       { type: "triangle", notes: [{ f: 1174.7, t: 0 }, { f: 1567.98, t: 0.08 }], peak: 0.30, tail: 0.20 },
  celebratory: { type: "sine",     notes: [{ f: 523.25, t: 0 }, { f: 659.25, t: 0.10 }, { f: 783.99, t: 0.20 }, { f: 1046.5, t: 0.30 }], peak: 0.24, tail: 0.30 },
};

function playChime(variant = "soft") {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const preset = CHIME_PRESETS[variant] || CHIME_PRESETS.soft;
    const ctx = new AC();
    const now = ctx.currentTime;
    preset.notes.forEach(({ f, t }) => {
      const o = ctx.createOscillator();
      o.type = preset.type; o.frequency.value = f;
      const gg = ctx.createGain();
      gg.gain.setValueAtTime(0.0001, now + t);
      gg.gain.exponentialRampToValueAtTime(preset.peak, now + t + 0.02);
      gg.gain.exponentialRampToValueAtTime(0.0001, now + t + preset.tail);
      o.connect(gg); gg.connect(ctx.destination);
      o.start(now + t); o.stop(now + t + preset.tail + 0.05);
    });
    setTimeout(() => { try { ctx.close(); } catch {} }, 1200);
  } catch { /* silent */ }
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [muted, setMuted] = useState(false);
  const [soundMuted, setSoundMuted] = useState(false);
  const [chime, setChime] = useState("soft");
  const [showPrefs, setShowPrefs] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch { /* silent */ }
  };

  const loadPrefs = async () => {
    try {
      const r = await api.get("/notifications/preferences");
      setMuted(!!r.data.muted);
      setSoundMuted(!!r.data.sound_muted);
      setChime(r.data.chime || "soft");
    } catch {}
  };

  useEffect(() => {
    loadPrefs();
    load();
    const t = setInterval(() => { if (!muted) load(); }, 30000);
    return () => clearInterval(t);
  }, [muted]);

  // Toast on new arrivals (compare against previously-seen ids)
  const seenRef = useRef(null);
  useEffect(() => {
    if (muted) { seenRef.current = new Set(items.map(i => i.id)); return; }
    if (seenRef.current === null) {
      seenRef.current = new Set(items.map(i => i.id));
      return;
    }
    const seen = seenRef.current;
    const fresh = items.filter(i => !seen.has(i.id) && !i.read);
    if (fresh.length > 0) {
      const first = fresh[0];
      if (!soundMuted) playChime(chime);
      toast.success(first.message, {
        description: fresh.length > 1 ? `+${fresh.length - 1} more new` : undefined,
        action: first.wa_url ? {
          label: "Ping WA",
          onClick: () => window.open(first.wa_url, "_blank", "noopener,noreferrer"),
        } : undefined,
      });
    }
    seenRef.current = new Set(items.map(i => i.id));
  }, [items, muted, soundMuted, chime]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setShowPrefs(false); } };
    if (open) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); setItems(items.map(i => ({...i, read: true}))); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      setUnread(u => Math.max(0, u - 1));
      setItems(items.map(i => i.id === n.id ? {...i, read: true} : i));
    }
  };

  const pingWA = (n, e) => {
    e.stopPropagation();
    openItem(n);
    window.open(n.wa_url, "_blank", "noopener,noreferrer");
  };

  const toggleMute = async (next) => {
    setMuted(next);
    try {
      await api.post("/notifications/preferences", { muted: next });
      toast.success(next ? "In-app notifications muted" : "Notifications enabled");
    } catch (e) { toast.error(errorMessage(e)); setMuted(!next); }
  };

  const toggleSound = async (next) => {
    setSoundMuted(next);
    try {
      await api.post("/notifications/preferences", { sound_muted: next });
      if (!next) playChime(chime);
    } catch (e) { toast.error(errorMessage(e)); setSoundMuted(!next); }
  };

  const pickChime = async (v) => {
    setChime(v);
    playChime(v);   // preview
    try { await api.post("/notifications/preferences", { chime: v }); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const timeAgo = (iso) => {
    try {
      const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
      if (s < 60) return `${Math.floor(s)}s ago`;
      if (s < 3600) return `${Math.floor(s/60)}m ago`;
      if (s < 86400) return `${Math.floor(s/3600)}h ago`;
      return `${Math.floor(s/86400)}d ago`;
    } catch { return ""; }
  };

  return (
    <div ref={ref} className="relative" data-testid="notif-bell-root">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="notif-bell-btn"
        className={`relative w-10 h-10 rounded-full border border-white/10 bg-slate-900/80 hover:bg-white/5 flex items-center justify-center transition-colors ${muted ? "text-slate-600" : "text-slate-300 hover:text-amber-400"}`}
        aria-label="Notifications"
        title={muted ? "Notifications muted" : "Notifications"}
      >
        {muted ? <BellOff className="w-4 h-4"/> : <Bell className="w-4 h-4"/>}
        {!muted && unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold px-1 border border-slate-950">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[420px] max-w-[92vw] rounded-xl bg-slate-950 border border-white/10 shadow-2xl z-50 overflow-hidden" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="font-heading text-white text-sm">Notifications</div>
            <div className="flex items-center gap-2">
              {unread > 0 && !muted && (
                <button onClick={markAllRead} data-testid="notif-mark-all-btn" className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-amber-400 flex items-center gap-1">
                  <CheckCheck className="w-3 h-3"/> Mark all read
                </button>
              )}
              <button onClick={() => setShowPrefs(v => !v)} data-testid="notif-prefs-btn" title="Preferences" className="text-slate-500 hover:text-amber-400"><Settings2 className="w-4 h-4"/></button>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4"/></button>
            </div>
          </div>

          {showPrefs && (
            <div className="px-4 py-3 border-b border-white/5 bg-slate-900/60 space-y-3" data-testid="notif-prefs-panel">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm text-white">Mute in-app notifications</div>
                  <div className="text-[11px] text-slate-500">Stop new lead pings from landing in your bell. You will still see them in All Leads.</div>
                </div>
                <div className="relative shrink-0">
                  <input type="checkbox" checked={muted} onChange={e => toggleMute(e.target.checked)} className="sr-only peer" data-testid="notif-mute-toggle"/>
                  <div className="w-12 h-7 bg-slate-800 rounded-full peer-checked:bg-amber-500 transition-colors relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"/>
                </div>
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-start gap-2">
                  {soundMuted ? <VolumeX className="w-4 h-4 text-slate-500 mt-0.5"/> : <Volume2 className="w-4 h-4 text-amber-400 mt-0.5"/>}
                  <div>
                    <div className="text-sm text-white">Mute sound chime</div>
                    <div className="text-[11px] text-slate-500">A soft chime plays when a new lead lands. Toggle off to keep quiet.</div>
                  </div>
                </div>
                <div className="relative shrink-0">
                  <input type="checkbox" checked={soundMuted} onChange={e => toggleSound(e.target.checked)} className="sr-only peer" data-testid="notif-sound-toggle"/>
                  <div className="w-12 h-7 bg-slate-800 rounded-full peer-checked:bg-slate-600 transition-colors relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"/>
                </div>
              </label>

              <div className={soundMuted ? "opacity-40 pointer-events-none" : ""}>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Chime style</div>
                <div className="grid grid-cols-3 gap-2" data-testid="chime-picker">
                  {[
                    { key: "soft",        label: "Soft",       desc: "Gentle" },
                    { key: "sharp",       label: "Sharp",      desc: "Alert" },
                    { key: "celebratory", label: "Celebratory",desc: "4-note" },
                  ].map(c => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => pickChime(c.key)}
                      data-testid={`chime-${c.key}`}
                      className={`px-2 py-2 rounded-lg border text-center transition-colors ${chime === c.key ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"}`}
                    >
                      <div className="text-xs font-bold">{c.label}</div>
                      <div className="text-[9px] uppercase tracking-widest opacity-70">{c.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="max-h-[420px] overflow-y-auto divide-y divide-white/5">
            {items.length === 0 && (
              <div className="p-6 text-sm text-slate-500 text-center" data-testid="notif-empty">
                {muted ? "Notifications muted." : "No notifications yet."}
              </div>
            )}
            {items.map(n => (
              <div key={n.id} onClick={() => openItem(n)} className={`px-4 py-3 hover:bg-white/5 cursor-pointer ${n.read ? "" : "bg-amber-500/[0.03]"}`} data-testid={`notif-item-${n.id}`}>
                <div className="flex items-start gap-3 mb-2">
                  <div className="mt-1">
                    <span className={`inline-block w-2 h-2 rounded-full ${n.read ? "bg-slate-700" : "bg-amber-400"}`}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{n.message}</div>
                    {n.loan_type && (
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {n.loan_type}{n.amount ? ` · ₹${Number(n.amount).toLocaleString("en-IN")}` : ""}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-600 mt-1 uppercase tracking-widest">{timeAgo(n.created_at)} · by {n.by_name}</div>
                  </div>
                </div>
                {n.wa_url && (
                  <button
                    onClick={(e) => pingWA(n, e)}
                    data-testid={`notif-wa-${n.id}`}
                    className="w-full flex items-center justify-center gap-2 text-xs text-emerald-400 border border-emerald-500/30 rounded-full py-1.5 hover:bg-emerald-500/10 transition-colors"
                  >
                    <MessageSquare className="w-3 h-3"/> Ping via WhatsApp
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
